import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { apiFetch } from "../../services/api";
import { formatMoney, formatDate } from "../../utils/formatters";
import {
  adminOrderStatusMeta,
  adminPaymentStatusMeta,
  exportOrdersCsv,
} from "../../utils/orderHelpers";
import {
  ADMIN_ORDER_STATUS_OPTIONS,
  ADMIN_ORDER_TIMELINE_STAGES,
  ADMIN_PAYMENT_STATUS_META,
} from "../../constants/orders";
import Badge from "../../components/Badge";

function AdminOrdersToolbar({
  adminTheme,
  searchInput,
  onSearchChange,
  status,
  onStatusChange,
  paymentStatus,
  onPaymentStatusChange,
  paymentMethod,
  onPaymentMethodChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  sort,
  onSortChange,
  selectedCount,
  onBulkStatus,
  onExportCsv,
}) {
  const selectStyle = {
    padding: "10px 12px",
    border: `1px solid ${adminTheme.border}`,
    borderRadius: 10,
    background: adminTheme.surface,
    color: adminTheme.ink,
    fontSize: 13,
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1fr 1fr 1fr",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <input
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search order ID, customer, email or phone…"
          style={{ ...selectStyle, gridColumn: "span 1" }}
        />
        <select value={status} onChange={(e) => onStatusChange(e.target.value)} style={selectStyle}>
          <option value="">All statuses</option>
          {ADMIN_ORDER_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {adminOrderStatusMeta(s).label}
            </option>
          ))}
          <option value="refunded">Refunded</option>
        </select>
        <select
          value={paymentStatus}
          onChange={(e) => onPaymentStatusChange(e.target.value)}
          style={selectStyle}
        >
          <option value="">All payment statuses</option>
          {Object.keys(ADMIN_PAYMENT_STATUS_META).map((s) => (
            <option key={s} value={s}>
              {adminPaymentStatusMeta(s).label}
            </option>
          ))}
        </select>
        <select
          value={paymentMethod}
          onChange={(e) => onPaymentMethodChange(e.target.value)}
          style={selectStyle}
        >
          <option value="">All payment methods</option>
          <option value="card">Card</option>
          <option value="cod">Cash on delivery</option>
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => onDateFromChange(e.target.value)}
          style={selectStyle}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => onDateToChange(e.target.value)}
          style={selectStyle}
        />
        <select value={sort} onChange={(e) => onSortChange(e.target.value)} style={selectStyle}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="highest">Highest amount</option>
          <option value="lowest">Lowest amount</option>
        </select>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
          background: selectedCount > 0 ? adminTheme.goldSoft : "transparent",
          borderRadius: 10,
          padding: selectedCount > 0 ? "10px 14px" : 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {selectedCount > 0 && (
            <>
              <span style={{ fontWeight: 700, color: adminTheme.ink, fontSize: 13 }}>
                {selectedCount} selected
              </span>
              {[
                { key: "processing", label: "Mark Processing" },
                { key: "packed", label: "Mark Packed" },
                { key: "shipped", label: "Mark Shipped" },
                { key: "delivered", label: "Mark Delivered" },
                { key: "cancelled", label: "Cancel" },
              ].map((a) => (
                <button
                  key={a.key}
                  onClick={() => onBulkStatus(a.key)}
                  style={{
                    border: `1px solid ${adminTheme.gold}`,
                    background: "#fff",
                    color: adminTheme.ink,
                    borderRadius: 8,
                    padding: "6px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {a.label}
                </button>
              ))}
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onExportCsv}
            title="Exports the currently loaded page of orders"
            style={{
              border: `1px solid ${adminTheme.gold}`,
              background: adminTheme.ink,
              color: adminTheme.gold,
              borderRadius: 8,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Export CSV
          </button>
          {/* TODO: Excel export — requires a backend xlsx-generation endpoint, not implemented yet */}
          <button
            disabled
            title="Coming soon"
            style={{
              border: `1px solid ${adminTheme.border}`,
              background: "#f2f2ef",
              color: adminTheme.muted,
              borderRadius: 8,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "not-allowed",
            }}
          >
            Export Excel
          </button>
          {/* TODO: PDF export — requires a backend PDF-generation endpoint, not implemented yet */}
          <button
            disabled
            title="Coming soon"
            style={{
              border: `1px solid ${adminTheme.border}`,
              background: "#f2f2ef",
              color: adminTheme.muted,
              borderRadius: 8,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "not-allowed",
            }}
          >
            Export PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminOrderDrawer({ adminTheme, order, loading, onClose, token, toast, onUpdated }) {
  const [statusValue, setStatusValue] = useState(order?.status || "pending");
  const [savingStatus, setSavingStatus] = useState(false);
  const [notesValue, setNotesValue] = useState(order?.admin_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    setStatusValue(order?.status || "pending");
    setNotesValue(order?.admin_notes || "");
  }, [order?.id, order?.status, order?.admin_notes]);

  const saveStatus = async () => {
    if (!order) return;
    setSavingStatus(true);
    try {
      const res = await apiFetch(
        `/admin/orders/${order.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: statusValue }),
        },
        { authToken: token },
      );
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error || "Unable to update order status");
      toast("Order status updated");
      onUpdated({ ...order, status: statusValue });
    } catch (e) {
      toast(e.message || "Unable to update order status", "error");
    } finally {
      setSavingStatus(false);
    }
  };

  const saveNotes = async () => {
    if (!order) return;
    setSavingNotes(true);
    try {
      const res = await apiFetch(
        `/admin/orders/${order.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ admin_notes: notesValue }),
        },
        { authToken: token },
      );
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error || "Unable to save notes");
      toast("Admin notes saved");
      onUpdated({ ...order, admin_notes: notesValue });
    } catch (e) {
      toast(e.message || "Unable to save notes", "error");
    } finally {
      setSavingNotes(false);
    }
  };

  const sectionStyle = {
    background: adminTheme.surface,
    border: `1px solid ${adminTheme.border}`,
    borderRadius: 14,
    padding: 16,
    marginBottom: 14,
  };
  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: adminTheme.gold,
    margin: "0 0 8px",
  };
  const rowStyle = { display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 };

  const timelineIndex = order ? ADMIN_ORDER_TIMELINE_STAGES.indexOf(order.status) : -1;
  const paymentConfirmed = order?.payment_status === "paid";
  const isCancelled = order?.status === "cancelled";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        onClick={onClose}
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }}
      />
      <div
        style={{
          position: "relative",
          width: "min(560px, 100%)",
          height: "100%",
          background: "#fff",
          overflowY: "auto",
          padding: 24,
          boxShadow: "-12px 0 32px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <h2 style={{ margin: 0, fontFamily: "'Georgia', serif", color: adminTheme.ink }}>
            Order #{order?.id ?? "…"}
          </h2>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              cursor: "pointer",
              color: adminTheme.ink,
            }}
          >
            ✕
          </button>
        </div>

        {loading || !order ? (
          <>
            <Skeleton height={80} style={{ marginBottom: 14 }} />
            <Skeleton height={140} style={{ marginBottom: 14 }} />
            <Skeleton height={140} />
          </>
        ) : (
          <>
            <div style={sectionStyle}>
              <p style={labelStyle}>Customer</p>
              <div style={rowStyle}>
                <span>Name</span>
                <strong>{`${order.first_name || ""} ${order.last_name || ""}`.trim() || "—"}</strong>
              </div>
              <div style={rowStyle}>
                <span>Email</span>
                <strong>{order.email || "—"}</strong>
              </div>
              <div style={rowStyle}>
                <span>Phone</span>
                <strong>{order.phone || "—"}</strong>
              </div>
            </div>

            <div style={sectionStyle}>
              <p style={labelStyle}>Shipping Address</p>
              <div style={rowStyle}>
                <span>Recipient Name</span>
                <strong>{order.shipping_name || "—"}</strong>
              </div>
              <div style={rowStyle}>
                <span>Street</span>
                <strong>
                  {[order.shipping_line1, order.shipping_line2].filter(Boolean).join(", ") || "—"}
                </strong>
              </div>
              <div style={rowStyle}>
                <span>City</span>
                <strong>{order.shipping_city || "—"}</strong>
              </div>
              <div style={rowStyle}>
                <span>Postal Code</span>
                <strong>{order.shipping_post || "—"}</strong>
              </div>
            </div>

            <div style={sectionStyle}>
              <p style={labelStyle}>Products</p>
              {(order.items || []).map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 0",
                    borderBottom: `1px solid ${adminTheme.border}`,
                  }}
                >
                  <img
                    src={item.product_image_url || "/images/product-placeholder.png"}
                    alt={item.name}
                    style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{item.name}</div>
                    <div style={{ fontSize: 12, color: adminTheme.muted }}>Qty {item.qty}</div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 13 }}>
                    <div>{formatMoney(item.price)}</div>
                    <div style={{ color: adminTheme.muted }}>{formatMoney(item.subtotal)}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={sectionStyle}>
              <p style={labelStyle}>Price Breakdown</p>
              <div style={rowStyle}>
                <span>Subtotal</span>
                <span>{formatMoney(order.subtotal)}</span>
              </div>
              <div style={rowStyle}>
                <span>Shipping</span>
                <span>{formatMoney(order.shipping_cost)}</span>
              </div>
              <div style={rowStyle}>
                <span>Discount</span>
                <span>-{formatMoney(order.discount)}</span>
              </div>
              <div style={rowStyle}>
                <span>Tax</span>
                <span>{formatMoney(order.tax)}</span>
              </div>
              <div style={{ ...rowStyle, fontWeight: 800, borderTop: `1px solid ${adminTheme.border}`, marginTop: 4, paddingTop: 8 }}>
                <span>Grand Total</span>
                <span>{formatMoney(order.total)}</span>
              </div>
            </div>

            <div style={sectionStyle}>
              <p style={labelStyle}>Payment</p>
              <div style={rowStyle}>
                <span>Method</span>
                <strong>{order.payment_method || "—"}</strong>
              </div>
              <div style={rowStyle}>
                <span>Status</span>
                <Badge {...adminPaymentStatusMeta(order.payment_status)} label={adminPaymentStatusMeta(order.payment_status).label} />
              </div>
              <div style={rowStyle}>
                <span>Transaction ID</span>
                <strong>{order.payment_ref || "—"}</strong>
              </div>
              <div style={rowStyle}>
                <span>Payment Date</span>
                <strong>{paymentConfirmed ? formatDate(order.created_at) : "—"}</strong>
              </div>
            </div>

            <div style={sectionStyle}>
              <p style={labelStyle}>Shipping</p>
              <div style={rowStyle}>
                <span>Courier</span>
                <strong>{order.courier_name || "—"}</strong>
              </div>
              <div style={rowStyle}>
                <span>Tracking Number</span>
                <strong>{order.tracking_number || "—"}</strong>
              </div>
              <div style={rowStyle}>
                <span>Shipping Date</span>
                <strong>{formatDate(order.shipped_at)}</strong>
              </div>
              <div style={rowStyle}>
                <span>Estimated Delivery</span>
                <strong>{formatDate(order.estimated_delivery)}</strong>
              </div>
            </div>

            <div style={sectionStyle}>
              <p style={labelStyle}>Timeline</p>
              {isCancelled ? (
                <Badge label="Order Cancelled" color="#7a4b3a" />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { key: "placed", label: "Order Placed", done: true },
                    { key: "payment", label: "Payment Confirmed", done: paymentConfirmed },
                    { key: "processing", label: "Processing", done: timelineIndex >= 1 },
                    { key: "packed", label: "Packed", done: timelineIndex >= 2 },
                    { key: "shipped", label: "Shipped", done: timelineIndex >= 3 },
                    { key: "delivered", label: "Delivered", done: timelineIndex >= 4 },
                  ].map((step) => (
                    <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          background: step.done ? adminTheme.gold : adminTheme.border,
                          color: step.done ? "#fff" : adminTheme.muted,
                        }}
                      >
                        {step.done ? "✓" : ""}
                      </span>
                      <span style={{ color: step.done ? adminTheme.ink : adminTheme.muted, fontWeight: step.done ? 700 : 400 }}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={sectionStyle}>
              <p style={labelStyle}>Status Management</p>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    border: `1px solid ${adminTheme.border}`,
                    borderRadius: 10,
                  }}
                >
                  {ADMIN_ORDER_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {adminOrderStatusMeta(s).label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={saveStatus}
                  disabled={savingStatus || statusValue === order.status}
                  style={{
                    border: "none",
                    background: adminTheme.ink,
                    color: adminTheme.gold,
                    borderRadius: 10,
                    padding: "10px 18px",
                    fontWeight: 700,
                    cursor: savingStatus ? "wait" : "pointer",
                  }}
                >
                  {savingStatus ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            <div style={sectionStyle}>
              <p style={labelStyle}>Admin Notes (private)</p>
              <textarea
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 10,
                  border: `1px solid ${adminTheme.border}`,
                  marginBottom: 8,
                  resize: "vertical",
                }}
              />
              <button
                onClick={saveNotes}
                disabled={savingNotes}
                style={{
                  border: `1px solid ${adminTheme.gold}`,
                  background: "#fff",
                  color: adminTheme.ink,
                  borderRadius: 10,
                  padding: "8px 16px",
                  fontWeight: 700,
                  cursor: savingNotes ? "wait" : "pointer",
                }}
              >
                {savingNotes ? "Saving…" : "Save Notes"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminOrdersPage() {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const adminTheme = {
    ink: "#111110",
    muted: "#888780",
    border: "rgba(0,0,0,0.06)",
    surface: "#FBFAF8",
    gold: "#C49A3C",
    goldSoft: "#F5EDD6",
    danger: "#A94442",
  };

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPageNum] = useState(1);
  const limit = 25;

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [drawerOrderId, setDrawerOrderId] = useState(null);
  const [orderCache, setOrderCache] = useState({});
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Debounce the free-text search input before it triggers a fetch.
  useEffect(() => {
    const t = setTimeout(() => {
      setPageNum(1);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let mounted = true;
    setStatsLoading(true);
    apiFetch("/admin/orders/stats", {}, { authToken: token })
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.error || "Unable to load order stats");
        return body;
      })
      .then((body) => {
        if (mounted) setStats(body);
      })
      .catch(() => {
        if (mounted) setStats(null);
      })
      .finally(() => {
        if (mounted) setStatsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [user, token]);

  const reloadStats = () => {
    if (!user || user.role !== "admin") return;
    apiFetch("/admin/orders/stats", {}, { authToken: token })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => body && setStats(body))
      .catch(() => {});
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let mounted = true;
    setOrdersLoading(true);
    setListError("");
    const params = new URLSearchParams({ page: String(page), limit: String(limit), sort });
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (paymentStatus) params.set("payment_status", paymentStatus);
    if (paymentMethod) params.set("payment_method", paymentMethod);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);

    apiFetch(`/admin/orders/search?${params.toString()}`, {}, { authToken: token })
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (r.status === 401) throw new Error("Your session has expired — please sign in again.");
        if (!r.ok) throw new Error(body?.error || "Unable to load orders");
        return body;
      })
      .then((body) => {
        if (!mounted) return;
        setOrders(body?.data || []);
        setTotal(body?.total || 0);
      })
      .catch((e) => {
        if (!mounted) return;
        setOrders([]);
        setTotal(0);
        setListError(e.message || "Unable to load orders. Please try again.");
      })
      .finally(() => {
        if (mounted) setOrdersLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [user, token, search, status, paymentStatus, paymentMethod, dateFrom, dateTo, sort, page]);

  const patchRows = (ids, changes) => {
    setOrders((prev) => prev.map((o) => (ids.includes(o.id) ? { ...o, ...changes } : o)));
  };

  const openOrder = async (id) => {
    setDrawerOrderId(id);
    if (orderCache[id]) return;
    setDrawerLoading(true);
    try {
      const res = await apiFetch(`/admin/orders/${id}`, {}, { authToken: token });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error || "Unable to load order details");
      setOrderCache((c) => ({ ...c, [id]: body }));
    } catch (e) {
      toast(e.message || "Unable to load order details", "error");
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleOrderUpdated = (updatedOrder) => {
    setOrderCache((c) => ({ ...c, [updatedOrder.id]: updatedOrder }));
    patchRows([updatedOrder.id], { status: updatedOrder.status, admin_notes: updatedOrder.admin_notes });
    reloadStats();
  };

  const runBulkStatus = async (newStatus) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const res = await apiFetch(
        "/admin/orders/bulk",
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_ids: ids, status: newStatus }),
        },
        { authToken: token },
      );
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error || "Bulk update failed");
      toast(`Updated ${ids.length} order${ids.length > 1 ? "s" : ""}`);
      patchRows(ids, { status: newStatus });
      setSelectedIds(new Set());
      reloadStats();
    } catch (e) {
      toast(e.message || "Bulk update failed", "error");
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === orders.length ? new Set() : new Set(orders.map((o) => o.id))));
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const statCards = [
    { label: "Total Orders", value: stats?.total_orders },
    { label: "Pending", value: stats?.pending_orders },
    { label: "Processing", value: stats?.processing_orders },
    { label: "Packed", value: stats?.packed_orders },
    { label: "Shipped", value: stats?.shipped_orders },
    { label: "Delivered", value: stats?.delivered_orders },
    { label: "Cancelled", value: stats?.cancelled_orders },
    { label: "Today's Orders", value: stats?.today_orders },
    { label: "Today's Revenue", value: stats != null ? formatMoney(stats.today_revenue) : undefined },
    { label: "Total Revenue", value: stats != null ? formatMoney(stats.total_revenue) : undefined },
  ];

  const thStyle = {
    position: "sticky",
    top: 0,
    background: adminTheme.surface,
    color: adminTheme.muted,
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    padding: "10px 12px",
    borderBottom: `2px solid ${adminTheme.border}`,
    zIndex: 1,
  };
  const tdStyle = { padding: "10px 12px", fontSize: 13, borderBottom: `1px solid ${adminTheme.border}` };

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: "42px 24px 70px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 20,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <p
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: adminTheme.gold,
              margin: "0 0 6px",
            }}
          >
            Admin Console
          </p>
          <h1 style={{ fontFamily: "'Georgia', serif", fontSize: 36, color: adminTheme.ink, margin: 0 }}>
            Orders Management
          </h1>
          <p style={{ margin: "10px 0 0", color: adminTheme.muted }}>
            Search, filter, and manage every order from one place.
          </p>
        </div>
        <button
          onClick={() => navigate("/home")}
          style={{
            background: adminTheme.surface,
            border: `2px solid ${adminTheme.gold}`,
            color: adminTheme.ink,
            borderRadius: 12,
            padding: "10px 16px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Exit admin view
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 12,
          marginBottom: 24,
        }}
      >
        {statCards.map((c) => (
          <div
            key={c.label}
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 14,
              padding: 16,
            }}
          >
            {statsLoading ? (
              <Skeleton height={36} />
            ) : (
              <>
                <div style={{ fontSize: 22, fontWeight: 800, color: adminTheme.ink }}>
                  {c.value ?? "—"}
                </div>
                <div style={{ fontSize: 12, color: adminTheme.muted, marginTop: 4 }}>{c.label}</div>
              </>
            )}
          </div>
        ))}
      </div>

      <AdminOrdersToolbar
        adminTheme={adminTheme}
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        status={status}
        onStatusChange={(v) => { setPageNum(1); setStatus(v); }}
        paymentStatus={paymentStatus}
        onPaymentStatusChange={(v) => { setPageNum(1); setPaymentStatus(v); }}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={(v) => { setPageNum(1); setPaymentMethod(v); }}
        dateFrom={dateFrom}
        onDateFromChange={(v) => { setPageNum(1); setDateFrom(v); }}
        dateTo={dateTo}
        onDateToChange={(v) => { setPageNum(1); setDateTo(v); }}
        sort={sort}
        onSortChange={(v) => { setPageNum(1); setSort(v); }}
        selectedCount={selectedIds.size}
        onBulkStatus={runBulkStatus}
        onExportCsv={() => exportOrdersCsv(orders)}
      />

      {listError && (
        <div
          style={{
            background: "#fdecea",
            border: `1px solid ${adminTheme.danger}`,
            color: adminTheme.danger,
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          {listError}
        </div>
      )}

      <div
        style={{
          background: "#fff",
          border: `1px solid ${adminTheme.border}`,
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        <div style={{ maxHeight: 620, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>
                  <input
                    type="checkbox"
                    checked={orders.length > 0 && selectedIds.size === orders.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={thStyle}>Order ID</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Order Date</th>
                <th style={thStyle}>Items</th>
                <th style={thStyle}>Total</th>
                <th style={thStyle}>Payment Method</th>
                <th style={thStyle}>Payment Status</th>
                <th style={thStyle}>Order Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ordersLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td style={tdStyle} colSpan={11}>
                      <Skeleton height={24} />
                    </td>
                  </tr>
                ))
              ) : orders.length === 0 ? (
                <tr>
                  <td style={tdStyle} colSpan={11}>
                    <div style={{ textAlign: "center", padding: "50px 0", color: adminTheme.muted }}>
                      <div style={{ fontSize: 40, marginBottom: 10 }}>📦</div>
                      <div style={{ fontWeight: 700, color: adminTheme.ink, marginBottom: 4 }}>
                        No Orders Found
                      </div>
                      <div style={{ fontSize: 13 }}>Try adjusting your search or filters.</div>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr
                    key={o.id}
                    style={{ cursor: "default" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = adminTheme.goldSoft)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={tdStyle}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(o.id)}
                        onChange={() => toggleSelected(o.id)}
                      />
                    </td>
                    <td style={tdStyle}>#{o.id}</td>
                    <td style={tdStyle}>{`${o.first_name || ""} ${o.last_name || ""}`.trim() || "—"}</td>
                    <td style={tdStyle}>{o.email || "—"}</td>
                    <td style={tdStyle}>{formatDate(o.created_at)}</td>
                    <td style={tdStyle}>{o.item_count ?? "—"}</td>
                    <td style={tdStyle}>{formatMoney(o.total)}</td>
                    <td style={tdStyle}>{o.payment_method || "—"}</td>
                    <td style={tdStyle}>
                      <Badge {...adminPaymentStatusMeta(o.payment_status)} />
                    </td>
                    <td style={tdStyle}>
                      <Badge {...adminOrderStatusMeta(o.status)} />
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => openOrder(o.id)}
                        style={{
                          border: `1px solid ${adminTheme.gold}`,
                          background: "#fff",
                          color: adminTheme.ink,
                          borderRadius: 8,
                          padding: "6px 12px",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!ordersLoading && orders.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 16,
          }}
        >
          <span style={{ fontSize: 13, color: adminTheme.muted }}>
            Page {page} of {totalPages} · {total} order{total === 1 ? "" : "s"}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setPageNum((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{
                border: `1px solid ${adminTheme.border}`,
                background: "#fff",
                borderRadius: 8,
                padding: "8px 14px",
                cursor: page <= 1 ? "not-allowed" : "pointer",
                opacity: page <= 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>
            <button
              onClick={() => setPageNum((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{
                border: `1px solid ${adminTheme.border}`,
                background: "#fff",
                borderRadius: 8,
                padding: "8px 14px",
                cursor: page >= totalPages ? "not-allowed" : "pointer",
                opacity: page >= totalPages ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {drawerOrderId != null && (
        <AdminOrderDrawer
          adminTheme={adminTheme}
          order={orderCache[drawerOrderId]}
          loading={drawerLoading && !orderCache[drawerOrderId]}
          onClose={() => setDrawerOrderId(null)}
          token={token}
          toast={toast}
          onUpdated={handleOrderUpdated}
        />
      )}
    </div>
  );
}
